// src/components/auth/Register.tsx

import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Avatar,
    CircularProgress,
    Button,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { InvitationValidation } from '../../types';
import RegisterForm from './RegisterForm';

const Register: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { token } = useParams<{ token: string }>();

    const [invitationData, setInvitationData] = useState<InvitationValidation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const checkInvitation = async () => {
            if (!token) {
                setError('Регистрация возможна только по приглашению от репетитора.');
                setLoading(false);
                return;
            }

            try {
                const response = await authApi.validateInvitation(token);
                if (response.success && response.data) {
                    setInvitationData(response.data);
                } else {
                    setError(response.error || 'Ссылка-приглашение недействительна');
                }
            } catch (err: any) {
                setError(
                    err?.response?.data?.error
                    || 'Ссылка-приглашение недействительна или истекла. Попросите репетитора отправить новую.'
                );
            } finally {
                setLoading(false);
            }
        };

        checkInvitation();
    }, [token]);

    // Загрузка — показываем спиннер
    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // Ошибка или нет токена — показываем "только по приглашению"
    if (error || !invitationData || !token) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    p: 2,
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        width: '100%',
                        maxWidth: 500,
                        borderRadius: 2,
                        textAlign: 'center',
                    }}
                >
                    <Avatar
                        sx={{
                            bgcolor: 'warning.main',
                            width: 56,
                            height: 56,
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <LockIcon fontSize="large" />
                    </Avatar>

                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 600, mb: 1 }}>
                        Регистрация только по приглашению
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {error || 'Если вы ученик — попросите репетитора отправить вам ссылку-приглашение.'}
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => navigate('/login')}
                    >
                        Перейти к входу
                    </Button>
                </Paper>
            </Box>
        );
    }

    // Токен валиден — показываем форму
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 2,
                py: 4,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: { xs: 3, sm: 4 },
                    width: '100%',
                    maxWidth: 520,
                    borderRadius: 2,
                }}
            >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                        sx={{
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <PersonAddIcon fontSize="large" />
                    </Avatar>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 600, mb: 0.5 }}>
                        Регистрация
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Заполните данные для завершения регистрации
                    </Typography>
                </Box>

                <RegisterForm
                    invitationToken={token}
                    initialData={{
                        fullName: invitationData.studentFullName,
                        phone: invitationData.studentPhone || undefined,
                        telegram: invitationData.studentTelegram || undefined,
                        vk: invitationData.studentVk || undefined,
                    }}
                />
            </Paper>
        </Box>
    );
};

export default Register;